import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Warning, ArrowRight } from "@phosphor-icons/react";

export const UsageWarningBanner = () => {
  const { api, user } = useAuth();
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (!api || !user) return;
    const fetchUsage = async () => {
      try {
        const resp = await api.get("/usage");
        setUsage(resp.data);
      } catch { /* silent */ }
    };
    fetchUsage();
  }, [api, user]);

  if (!usage || usage.is_admin || !usage.generations_limit) return null;

  const pct = usage.generations_used / usage.generations_limit;
  const atLimit = usage.generations_used >= usage.generations_limit;
  const nearLimit = pct >= 0.8 && !atLimit;

  if (!nearLimit && !atLimit) return null;

  if (atLimit) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center justify-between gap-4"
        data-testid="usage-limit-banner"
      >
        <div className="flex items-start gap-3">
          <Warning size={18} weight="fill" className="text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-500">
              You've reached your {usage.generations_limit} generation limit for this month.
            </p>
            <p className="text-xs text-red-500/70 mt-0.5">Upgrade to keep creating.</p>
          </div>
        </div>
        <Link
          to="/pricing"
          className="shrink-0 px-4 py-2 rounded-md bg-red-500 text-white text-xs font-semibold hover:bg-red-600 transition-colors"
          data-testid="usage-upgrade-now-btn"
        >
          Upgrade Now
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 flex items-center justify-between gap-4"
      data-testid="usage-warning-banner"
    >
      <div className="flex items-start gap-3">
        <Warning size={18} weight="fill" className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-600 dark:text-amber-400">
          You've used <span className="font-semibold">{usage.generations_used}</span> of <span className="font-semibold">{usage.generations_limit}</span> generations this month.{" "}
          <Link to="/pricing" className="underline hover:no-underline font-medium" data-testid="usage-view-plans-link">
            View Plans <ArrowRight size={12} weight="bold" className="inline" />
          </Link>
        </p>
      </div>
    </motion.div>
  );
};
